import React, { useState, useEffect } from 'react';
import { getCenters } from '../../../services/supabaseService';
import { inviteUser } from '../../../services/invitationService';
import { useAuth } from '../../../components/hooks/useAuth';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Badge from '../../../components/ui/Badge';

const InviteUserModal = ({ onClose, onSuccess }) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    role: 'sales',
    expiresInDays: 7
  });
  const [centers, setCenters] = useState([]);
  const [selectedCenters, setSelectedCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchCenters();
  }, []);

  const fetchCenters = async () => {
    try {
      const data = await getCenters();
      setCenters(data || []);
    } catch (err) {
      console.error('Error fetching centers:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCenterToggle = (centerId) => {
    setSelectedCenters((prev) =>
      prev?.includes(centerId)
        ? prev?.filter((id) => id !== centerId)
        : [...prev, centerId]
    );
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Validate email
      if (!formData?.email || !formData?.email?.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      // Validate center selection for admin role
      if (formData?.role === 'admin' && selectedCenters?.length === 0) {
        throw new Error('Please select at least one center for Admin/Owner role');
      }

      // Determine parent admin ID
      let parentAdminId = null;
      if (userProfile?.role === 'admin') {
        // If current user is admin, they become the parent
        parentAdminId = userProfile?.id;
      } else if (formData?.role !== 'super_admin' && formData?.role !== 'admin') {
        // For center managers and sales, set parent as current user if they're admin
        parentAdminId = userProfile?.role === 'admin' ? userProfile?.id : null;
      }

      // Send invitation
      const result = await inviteUser({
        email: formData?.email,
        role: formData?.role,
        parentAdminId,
        centerIds: selectedCenters,
        expiresInDays: parseInt(formData?.expiresInDays)
      });

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to send invitation');
      }

      // Show success message
      setSuccess(true);
      
      // Show warning if email failed but invitation created
      if (result?.warning) {
        setError(result?.warning);
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        onSuccess?.();
      }, 2000);

    } catch (err) {
      console.error('Error sending invitation:', err);
      setError(err?.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const getRoleOptions = () => {
    if (userProfile?.role === 'super_admin') {
      return [
        { value: 'super_admin', label: 'Super Admin' },
        { value: 'admin', label: 'Admin/Owner' },
        { value: 'center_manager', label: 'Center Manager Sales' },
        { value: 'center_manager_ops', label: 'Center Manager Ops' },
        { value: 'sales', label: 'Sales' },
      ];
    } else if (userProfile?.role === 'admin') {
      return [
        { value: 'center_manager', label: 'Center Manager Sales' },
        { value: 'center_manager_ops', label: 'Center Manager Ops' },
        { value: 'sales', label: 'Sales' },
      ];
    }
    return [{ value: 'sales', label: 'Sales' }];
  };

  const shouldShowCenterSelection = () => {
    return ['admin', 'center_manager', 'center_manager_ops', 'sales']?.includes(formData?.role);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Invite New User</h2>
            <p className="text-sm text-gray-600 mt-1">Send an invitation email with role and center pre-assignment</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <Icon name="CheckCircle" size={20} className="text-green-500" />
              <div>
                <p className="text-green-700 font-medium">Invitation sent successfully!</p>
                <p className="text-green-600 text-sm mt-1">The user will receive an email with instructions to join.</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
              <Input
                type="email"
                name="email"
                value={formData?.email}
                onChange={handleChange}
                placeholder="user@example.com"
                required
                disabled={loading || success}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
              <Select 
                name="role" 
                value={formData?.role} 
                onChange={handleChange} 
                required
                disabled={loading || success}
              >
                {getRoleOptions()?.map(option => (
                  <option key={option?.value} value={option?.value}>{option?.label}</option>
                ))}
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                The user will be assigned this role when they accept the invitation
              </p>
            </div>

            {shouldShowCenterSelection() && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign Centers {formData?.role === 'admin' && '*'}
                </label>
                <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
                  {centers?.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No centers available</p>
                  ) : (
                    centers?.map((center) => (
                      <label
                        key={center?.id}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCenters?.includes(center?.id)}
                          onChange={() => handleCenterToggle(center?.id)}
                          disabled={loading || success}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{center?.name}</div>
                          <div className="text-sm text-gray-500">{center?.location}</div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
                {selectedCenters?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedCenters?.map((centerId) => {
                      const center = centers?.find((c) => c?.id === centerId);
                      return (
                        <Badge key={centerId} variant="blue">
                          {center?.name}
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  {formData?.role === 'admin' ?'Admin/Owner must be assigned to at least one center' :'Optional: Pre-assign centers for this user'}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invitation Expires In</label>
              <Select 
                name="expiresInDays" 
                value={formData?.expiresInDays} 
                onChange={handleChange}
                disabled={loading || success}
              >
                <option value="1">1 Day</option>
                <option value="3">3 Days</option>
                <option value="7">7 Days (Recommended)</option>
                <option value="14">14 Days</option>
                <option value="30">30 Days</option>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                The invitation link will expire after this period
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || success}
              className="flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Icon name="Loader" size={16} className="animate-spin" />
                  Sending...
                </>
              ) : success ? (
                <>
                  <Icon name="CheckCircle" size={16} />
                  Sent
                </>
              ) : (
                <>
                  <Icon name="Mail" size={16} />
                  Send Invitation
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;